import Immutable from 'immutable';
import { createSelector } from 'reselect';

import { selectAppConfig } from 'app/selectors/AppSelector';
import {
  selectBibEntities,
  selectSeriesEntryEntities,
  selectCardEntities,
  selectEventSeriesEntities,
  selectEventEntities
} from 'app/selectors/EntitiesSelector';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { getTerminalBranch } from 'app/selectors/LibrarySelector';
import { mapEventToEntity } from 'app/helpers/analytics/formatGA4Entity';

const getEventDetails = state => state.getIn(['events', 'eventsDetails', 'event'], Immutable.Map());
const selectConfigOverrides = state => state.get('analyticsGa4');

const selectBibEntity = createSelector(
  [selectBibEntities, selectSeriesEntryEntities, (_state, bibId) => bibId],
  (bibs, seriesBibs, bibId) => {
    if (!bibId) {
      return undefined;
    }

    const bib = bibs.get(bibId) ?? seriesBibs?.get(bibId);
    if (!bib) {
      return undefined;
    }

    return Immutable.Map({
      bib_audience: bib.getIn(['briefInfo', 'audiences', 0]) ?? undefined,
      bib_availability_status: bib.getIn(['availability', 'localisedStatus']) ?? undefined,
      bib_fiction_type: bib.getIn(['briefInfo', 'contentType']) ?? undefined,
      bib_format: bib.getIn(['briefInfo', 'format']) ?? undefined,
      bib_fulfillment_provider: bib.getIn(['policy', 'provider']) ?? undefined,
      bib_group_key: bib.getIn(['briefInfo', 'groupKey']) ?? undefined,
      bib_hold_count: bib.getIn(['availability', 'heldCopies']) ?? undefined,
      bib_metadata_id: bib.get('id') ?? undefined,
      bib_total_item_count: bib.getIn(['availability', 'totalCopies']) ?? undefined
    });
  }
);

const selectEventEntity = createSelector(
  [getEventDetails, selectEventEntities, selectEventSeriesEntities, (_state, eventId) => eventId],
  (event, events, eventSeriesEntities, eventId) => {
    if (!eventId) {
      return undefined;
    }

    const eventEntity = event?.isEmpty() ? events?.get(eventId) : event;
    if (!eventEntity) {
      return undefined;
    }

    const series = eventSeriesEntities.get(eventEntity?.get('seriesId'));
    return mapEventToEntity({ event: eventEntity, series });
  }
);

const selectCardEntity = createSelector([selectCardEntities, (_state, cardId) => cardId], (cards, cardId) => {
  if (!cardId) {
    return undefined;
  }

  const card = cards.get(cardId.toString());
  if (!card) {
    return undefined;
  }

  return Immutable.Map({
    card_id: card.get('id'),
    card_title: card.getIn(['title', 'text']),
    card_type: card.getIn(['contentType', 'slug']).replace('-', '_')
  });
});

const selectOrganizationEntity = createSelector([selectAppConfig], appConfig => {
  const subdomain = appConfig?.get('libraryDomain') ?? undefined;
  return Immutable.Map({ subdomain });
});

const selectProductEntity = createSelector([selectAppConfig, selectConfigOverrides], (appConfig, overrides) => {
  const currentURL = appConfig?.get('currentURL');
  const isMobile = appConfig?.get('mobileApp', false) ?? false;
  const overrideProductId = overrides?.get('productId') ?? null;
  const overrideProductVersion = overrides?.get('productVersion') ?? null;

  let productId = overrideProductId;
  if (overrideProductId) {
    productId = overrideProductId;
  } else if (isMobile) {
    productId = `apps_${appConfig?.get('mobileAppPlatform') ?? ''}`.toLowerCase();
  } else if (currentURL?.includes('/v2/events')) {
    productId = 'events';
  } else if (currentURL?.includes('/v2/locations')) {
    productId = 'web';
  } else {
    productId = 'core';
  }

  let productVersion;
  if (overrideProductVersion) {
    productVersion = overrideProductVersion;
  } else if (isMobile) {
    productVersion = appConfig?.get('mobileAppVersion');
  } else {
    productVersion = appConfig?.get('coreVersion');
  }

  return Immutable.Map({
    product_id: productId ?? undefined,
    product_version: productVersion ?? undefined
  });
});

const selectUserEntity = createSelector([selectCurrentUser, getTerminalBranch], (currentUser, terminalBranch) => {
  const analyticsId = currentUser?.get('encodedId');
  const isStaff = currentUser?.get('staff');

  let userType;
  if (isStaff === true) {
    userType = 'staff';
  } else if (isStaff === false) {
    userType = 'patron';
  }

  return Immutable.Map({
    user_analytics_id: analyticsId ?? undefined,
    user_location: terminalBranch ?? undefined,
    user_type: userType ?? undefined
  });
});

export {
  selectBibEntity,
  selectCardEntity,
  selectEventEntity,
  selectOrganizationEntity,
  selectProductEntity,
  selectUserEntity
};
